import { defineStore } from "pinia";
import { getLogout } from "@/api/login/LoginApi";
import router from "@/router";
import { setUserSidebar } from "@/app/util/route";
import type { LoginTypes } from "@/types/LoginTypes";

/**
 * 로그인 상태를 관리하는 Pinia 스토어
 *
 * @remarks
 * - 로그인 정보(loginInfo)를 보관
 * - 로그아웃/권한 체크 Util
 * @see {@link LoginTypes}
 */
export const useLoginStore = defineStore("login", {
  state: () => ({
    loginInfo: null as LoginTypes | null, // 로그인 정보
    sessionChk: false, // App load후 로그인 세션검사를 한 적이 있는지 여부
    apiAuth: "", // 현재 페이지 내 권한 정보 (C|R|U|D)
  }),
  actions: {
    /**
     * 로그인 정보 구축
     */
    async setLoginInfo(info: LoginTypes | null) {
      this.loginInfo = info;
    },
    /**
     * 세션 로그인 체크확인 여부
     */
    setSessionChk(state: boolean) {
      this.sessionChk = state;
    },
    async setApiAuth(auth: string) {
      this.apiAuth = auth;
    },
    /**
     * 로그아웃 처리
     *
     * @remarks
     * - 서버 logout 호출(getLogout) 후, 사이드바를 재설정하고 로그인 페이지로 이동합니다.
     * @see {@link getLogout}
     * @see {@link setUserSidebar}
     */
    async logout() {
      const res = await getLogout({ userDiv: this.loginInfo?.userDiv });
      if (res) {
        await this.setLoginInfo(null);

        // 일반 사용자: 기존 로직
        await setUserSidebar(false);
        router.push("/");
      }
    },

    /**
     * 권한 C 체크 (Create)
     *
     * @remarks
     * - loginInfo.apiAuth 문자열에서 'C' 권한 존재 여부를 판별
     */
    checkAuthC(): boolean {
      return this.apiAuth.split("|").includes("C") || false;
    },

    /**
     * 권한 R 체크 (Read)
     *
     * @remarks
     * - loginInfo.apiAuth 문자열에서 'R' 권한 존재 여부를 판별
     */
    checkAuthR() {
      return this.apiAuth.split("|").includes("R") || false;
    },

    /**
     * 권한 U 체크 (Update)
     *
     * @remarks
     * - loginInfo.apiAuth 문자열에서 'U' 권한 존재 여부를 판별
     */
    checkAuthU() {
      return this.apiAuth.split("|").includes("U") || false;
    },

    /**
     * 권한 D 체크 (Delete)
     *
     * @remarks
     * - loginInfo.apiAuth 문자열에서 'D' 권한 존재 여부를 판별
     */
    checkAuthD() {
      return this.apiAuth.split("|").includes("D") || false;
    },
  },
  getters: {
    /**
     * 현재 로그인 정보 반환
     *
     * @remarks
     * - LoginTypes 또는 null 반환
     * @see {@link LoginTypes}
     */
    getLoginInfo: (state) => state.loginInfo,
    getSessionChk: (state) => state.sessionChk,
    getApiAuth: (state) => state.apiAuth,
  },
});
